import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  useRefetchCatalogAndEntityOnNavigateFeature,
  useUserHasFeature,
} from 'src/hooks/useUserHasFeature';
import { DATA_REFRESH_RATE_IN_MILLIS_LONG } from 'src/utils/constants/constants';
import { isInFullPageEventView, isInSearchPage } from 'src/utils/deepLinkUtils';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';
import { getServerSideOnlyMetricsQuery } from 'src/utils/getServerSideOnlyMetricsQuery';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  CatalogClient,
  EntityWithTicketsQuery,
  EventWithData,
  Feature,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import {
  useCatalogDataContextDataSelector,
  useCatalogDataContextLogKeySelector,
} from '../CatalogDataContext';
import { getEventsWithNonZeroCounts } from '../CatalogDataContext/CatalogDataContext.utils';
import { useFilterQueryContextFilterQuerySelector } from '../FilterQueryContext/FilterQueryContext.selectors';
import {
  getStopwatchGapsForCatalogPageLoad,
  getStopwatchLoggingId,
} from '../StopwatchLoggingContext';
import { useStopwatchLoggingContext } from '../StopwatchLoggingContext/StopwatchLoggingContext';
import { StopwatchLoggingEventName } from '../StopwatchLoggingContext/StopwatchLoggingContext.types';
import {
  CatalogMetricsContextProviderProps,
  catalogMetricsContextWithSelector,
  CatalogMetricsResults,
  createCatalogMetricsContextV2,
  ICatalogMetricsContext,
} from './CatalogMetrics.type';

export function CatalogMetricsContextV2Provider<
  TMetrics extends { currency: string | null },
  TQuery extends EntityWithTicketsQuery & QueryWithViewMode,
>({
  queryKey,
  entityType,
  getCatalogMetrics,
  additionalTransformQuery,
  children,
  disabled,
  isDetailed,
}: CatalogMetricsContextProviderProps<TMetrics, TQuery>) {
  const hasCatalogDataContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const CatalogMetricsContext = createCatalogMetricsContextV2<TMetrics>();
  const CatalogMetricsContextWithSelector =
    catalogMetricsContextWithSelector<TMetrics>();

  const hasRefetchCatalogAndEntityOnNavigateFeature =
    useRefetchCatalogAndEntityOnNavigateFeature(entityType);

  const { trackError } = useClientLogContext();

  const data = useCatalogDataContextDataSelector();
  const logKey = useCatalogDataContextLogKeySelector();
  const [filterQuery] = useFilterQueryContextFilterQuerySelector<TQuery>();

  const { activeAccountWebClientConfig, mainRoute } = useAppContext();
  const { addStopGap } = useStopwatchLoggingContext();
  const isEventPage = isInFullPageEventView(mainRoute);
  const isSearchPage = isInSearchPage(mainRoute);

  const transformQuery = useCallback(
    (query: TQuery) => {
      const serverSideOnlyMetricsQuery = getServerSideOnlyMetricsQuery(query);
      if (additionalTransformQuery) {
        return additionalTransformQuery(serverSideOnlyMetricsQuery);
      } else {
        return serverSideOnlyMetricsQuery;
      }
    },
    [additionalTransformQuery]
  );

  const queryClient = useQueryClient();
  const transformedQuery = useMemo(
    () => transformQuery(filterQuery),
    [filterQuery, transformQuery]
  );

  const [metrics, setMetrics] =
    useState<CatalogMetricsResults<TMetrics, TQuery>>();
  const [lastRefetchTime, setLastRefetchTime] = useState<Date>();

  const getEventIdsToGetMetrics = (events: EventWithData[]) => {
    const eventIds = events
      .map((ev) => ev.counts?.viagVirtId ?? ev.event?.virtId)
      .filter((id: string | null): id is string => id != null);
    const performerIds = events
      .map((ev) => ev.counts?.perfId ?? ev.event?.perfId)
      .filter((id: number | null): id is number => id != null);
    const venueIds = events
      .map((ev) => ev.counts?.venId ?? ev.event?.venId)
      .filter((id: number | null): id is number => id != null);

    return {
      eventIds,
      performerIds,
      venueIds,
      maxRowVersions: undefined,
    };
  };

  const eventsNeedingMetrics = useMemo(() => {
    if (!data?.events) {
      return []; // don't need any metrics
    }

    const eventsWithdata = Object.values(data.events);
    const events = getEventsWithNonZeroCounts(entityType, eventsWithdata);

    return events;
  }, [data?.events, entityType]);

  const eventsMissingMetrics = useMemo(() => {
    const events = eventsNeedingMetrics;
    if (!events?.length) {
      return {}; // don't need any metrics
    }

    if (!metrics || !isEqual(metrics?.filter, transformedQuery)) {
      // no metrics, or metrics filter changed, need to re-get for all events
      return getEventIdsToGetMetrics(events);
    }

    // metrics filter didn't change, so just check if any event are missing metrics
    // if they are missing, go get them
    const results = events.filter((ev) =>
      isDetailed
        ? !metrics.eventsDetailed[ev.counts?.viagVirtId ?? ev.event?.virtId]
        : !metrics.events[ev.counts?.viagVirtId ?? ev.event?.virtId]
    );

    if (results.length) {
      // We have some missing metrics, so we need to get them
      return getEventIdsToGetMetrics(results);
    }

    // We don't have any missing metrics, but we want to get all if the time has ellapsed
    if (
      lastRefetchTime &&
      new Date().getTime() - lastRefetchTime.getTime() >
        DATA_REFRESH_RATE_IN_MILLIS_LONG
    ) {
      const maxRowVersions = metrics?.maxRowVersion; // if we're refreshing, we only want to get the new ones
      const eventIds = Object.keys(metrics?.events ?? {});
      const performerIds = Object.keys(metrics?.performers ?? {});
      const venueIds = Object.keys(metrics?.venues ?? {});

      return {
        eventIds,
        performerIds,
        venueIds,
        maxRowVersions: maxRowVersions ?? undefined,
      };
    }

    return {};
  }, [
    eventsNeedingMetrics,
    isDetailed,
    lastRefetchTime,
    metrics,
    transformedQuery,
  ]);

  const shouldQuery = useMemo(
    () =>
      !disabled &&
      Boolean(eventsMissingMetrics.eventIds?.length) &&
      !(!activeAccountWebClientConfig.activeAccountId || !getCatalogMetrics),
    [
      activeAccountWebClientConfig.activeAccountId,
      disabled,
      eventsMissingMetrics?.eventIds?.length,
      getCatalogMetrics,
    ]
  );

  const metricsQueryKey = useMemo(
    () => [
      queryKey,
      eventsMissingMetrics.eventIds?.toSorted(),
      transformedQuery,
      activeAccountWebClientConfig.activeAccountId,
    ],
    [
      queryKey,
      eventsMissingMetrics,
      transformedQuery,
      activeAccountWebClientConfig.activeAccountId,
    ]
  );

  const metricsQuery = useQuery({
    queryKey: metricsQueryKey,
    queryFn: () => {
      if (!shouldQuery) {
        return null;
      }

      return tryInvokeApi(
        () => {
          return getCatalogMetrics!(
            new CatalogClient(activeAccountWebClientConfig),
            {
              ...transformedQuery,
              // We are sending back the event ids we need metrics for - which can be a lot
              // TODO (SAPI-2030) - in the case where there's a lot of these, we should just send back
              // the event effecting filters and let the server figure out the events
              // Also - we need to send a row-version so refresh only update the changed entities
              eventOrMappingIds: eventsMissingMetrics.eventIds,
              performerIds: eventsMissingMetrics.performerIds,
              venueIds: eventsMissingMetrics.venueIds,
            },
            eventsMissingMetrics.maxRowVersions
          );
        },
        (error: ErrorTypes) => {
          trackError('CatalogClient.getCatalogMetrics', error, {
            ...transformedQuery,
          });
          // Since this is just loading metrics, we have the data so we'll just ignore that metrics error
        }
      );
    },
    enabled: shouldQuery,
    staleTime: hasRefetchCatalogAndEntityOnNavigateFeature ? 0 : Infinity, // If Infinity, we don't want query to calculate whether the data is stale because we're only refetch on an interval
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_LONG,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount:
      hasRefetchCatalogAndEntityOnNavigateFeature == null
        ? false
        : hasRefetchCatalogAndEntityOnNavigateFeature,
  });

  useEffect(() => {
    if (metricsQuery.data) {
      const newMetrics = {
        filter: transformedQuery,
        maxRowVersion: metricsQuery.data.maxRowVersion,
        eventsDetailed: {
          ...(metrics?.eventsDetailed ?? {}),
          ...metricsQuery.data.eventsDetailed,
        },
        events: {
          ...(metrics?.events ?? {}),
          ...metricsQuery.data.events,
        },
        performers: {
          ...(metrics?.performers ?? {}),
          ...metricsQuery.data.performers,
        },
        venues: {
          ...(metrics?.venues ?? {}),
          ...metricsQuery.data.venues,
        },
      };
      if (!isEqual(metrics, newMetrics)) {
        setLastRefetchTime(new Date());
        setMetrics(newMetrics);
      }
    }
  }, [metrics, metricsQuery.data, setMetrics, transformedQuery]);

  const refreshMetrics = useCallback(async () => {
    setMetrics(undefined); // clear the cached metrics
    await queryClient.invalidateQueries({
      queryKey: metricsQueryKey,
    });
  }, [metricsQueryKey, queryClient, setMetrics]);

  const contextValues = useMemo<ICatalogMetricsContext<TMetrics>>(() => {
    return {
      isLoading: metricsQuery.isLoading,
      eventDetailedMetrics: metrics?.eventsDetailed,
      eventMetrics: metrics?.events,
      performerMetrics: metrics?.performers,
      venueMetrics: metrics?.venues,
      refreshMetrics: refreshMetrics,
    };
  }, [
    metrics?.events,
    metrics?.eventsDetailed,
    metrics?.performers,
    metrics?.venues,
    metricsQuery.isLoading,
    refreshMetrics,
  ]);

  useEffect(() => {
    if (contextValues.eventMetrics != null) {
      const hasAllMetrics = eventsNeedingMetrics.every((ev) => {
        return (
          ev.event &&
          (contextValues.eventMetrics![ev.event.virtId] != null ||
            contextValues.eventDetailedMetrics![ev.event.virtId] != null)
        );
      });

      if (!disabled && hasAllMetrics) {
        addStopGap(
          logKey,
          getStopwatchLoggingId(mainRoute),
          getStopwatchGapsForCatalogPageLoad(isEventPage, isSearchPage),
          StopwatchLoggingEventName.EventMetricsLoad,
          entityType
        );
      }
    }
  }, [
    addStopGap,
    contextValues.eventDetailedMetrics,
    contextValues.eventMetrics,
    disabled,
    entityType,
    eventsMissingMetrics.eventIds,
    eventsNeedingMetrics,
    isEventPage,
    isSearchPage,
    logKey,
    mainRoute,
  ]);

  if (hasCatalogDataContextWithSelectorFeature) {
    return (
      <CatalogMetricsContextWithSelector.Provider value={contextValues}>
        {children}
      </CatalogMetricsContextWithSelector.Provider>
    );
  }

  return (
    <CatalogMetricsContext.Provider value={contextValues}>
      {children}
    </CatalogMetricsContext.Provider>
  );
}
